import React, { useState, useEffect } from 'react';
import { Control } from '@/services/mcmService';
import { Checkbox } from '@/components/ui/checkbox';
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/datatable";
import { Skeleton } from '@/components/ui/skeleton';
import { cleanDateAndTimeObject, toLocalDateString } from '@shared/renderDate';
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import * as assetsService from '@/services/assetsService';
import * as assuranceActionsService from '@/services/assuranceActionsService';
import * as risksService from '@/services/risksService';
import { MCMService } from '@/services/mcmService';
import * as documentsService from '@/services/documentsService';
import { useTenancy } from '@/contexts/TenancyContext';
import { getPolicyList } from '@/services/policyService';

type LinkedComponent = {
    confidence: number;
    domain: string;
    id: string;
    ref: string;
    relation: string;
    scheme: string;
    title: string;
};

type EnhancedComponent = LinkedComponent & {
    displayName?: string;
    isLoading?: boolean;
};

interface LinkedComponentsProps {
    components: LinkedComponent[];
    relatedAssuranceTasks?: string[];
}

const LinkedComponentsCell: React.FC<LinkedComponentsProps> = ({ components, relatedAssuranceTasks = [] }) => {
    const [expanded, setExpanded] = useState(false);
    const [enhancedComponents, setEnhancedComponents] = useState<EnhancedComponent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { activeTenancy } = useTenancy();

    // Effect to fetch component names when expanded
    useEffect(() => {
        if (expanded && components && components.length > 0 && enhancedComponents.length === 0) {
            fetchComponentDetails();
        }
    }, [expanded, components]);

    // Function to fetch component details
    const fetchComponentDetails = async () => {
        if (!components || components.length === 0) return;
        
        setIsLoading(true);
        
        // Initialize enhanced components with loading state
        const initialEnhanced = components.map(comp => ({
            ...comp,
            isLoading: true,
            displayName: comp.title || ''
        }));
        
        setEnhancedComponents(initialEnhanced);
        
        // Fetch details for each component
        const enhancedPromises = initialEnhanced.map(async (component) => {
            const id = component.ref || component.id;
            let displayName = component.title || '';
            
            if (id) {
                try {
                    switch(component.domain?.toLowerCase()) {
                        case 'asset':
                            try {
                                const asset = await assetsService.getAsset(id);
                                if (asset) {
                                    // Try various property paths where the name might be stored
                                    const assetAny = asset as any;
                                    if (assetAny.name) displayName = assetAny.name;
                                    else if (assetAny.assetName) displayName = assetAny.assetName;
                                    else if (assetAny.assetDesc) displayName = assetAny.assetDesc;
                                    else if (assetAny.entry?.assetName) displayName = assetAny.entry.assetName;
                                    else if (assetAny.entry?.assetDesc) displayName = assetAny.entry.assetDesc;
                                    else if (assetAny.details?.name) displayName = assetAny.details.name;
                                    else if (assetAny.details?.assetDesc) displayName = assetAny.details.assetDesc;
                                    else if (assetAny.data?.assetDesc) displayName = assetAny.data.assetDesc;
                                    else displayName = id;
                                }
                            } catch (err) {
                                console.warn(`Error fetching asset ${id}:`, err);
                                displayName = displayName || `Asset ${id}`;
                            }
                            break;
                            
                        case 'assurance':
                            if (displayName) break; // Skip if we already have a name
                            try {
                                const assuranceTasks = await assuranceActionsService.getAllTasks(activeTenancy?.id || '');
                                const task = assuranceTasks.find(t => t.assID === id);
                                if (task) displayName = task.checkDetail?.check || id;
                                else displayName = `Assurance Task ${id}`;
                            } catch (err) {
                                console.warn(`Error fetching assurance task ${id}:`, err);
                                displayName = displayName || `Assurance Task ${id}`;
                            }
                            break;
                            
                        case 'policy':
                            try {
                                const policies = await getPolicyList(activeTenancy?.id || '');
                                const policy = policies.find(p => p.id === id);
                                if (policy) displayName = policy.name || policy.policy_name || id;
                                else displayName = `Policy ${id}`;
                            } catch (err) {
                                console.warn(`Error fetching policy ${id}:`, err);
                                displayName = displayName || `Policy ${id}`;
                            }
                            break;
                            
                        case 'risk':
                            try {
                                if (activeTenancy?.id) {
                                    const risk = await risksService.getRisk(activeTenancy.id, id);
                                    if (risk?.risk) displayName = risk.risk;
                                    else if (risk?.description) displayName = risk.description;
                                    else if (risk?.entry?.risk) displayName = risk.entry.risk;
                                    else if (risk?.entry?.description) displayName = risk.entry.description;
                                    else if (risk?.details?.risk) displayName = risk.details.risk;
                                    else if (risk?.details?.description) displayName = risk.details.description;
                                    else displayName = displayName || id;
                                }
                            } catch (err) {
                                console.warn(`Error fetching risk ${id}:`, err);
                                displayName = displayName || `Risk ${id}`;
                            }
                            break;
                            
                        case 'control':
                            try {
                                const control = await MCMService.getControlById(id, activeTenancy?.id);
                                if (control) displayName = control.controlName || control.name || id;
                                else displayName = `Control ${id}`;
                            } catch (err) {
                                console.warn(`Error fetching control ${id}:`, err);
                                displayName = displayName || `Control ${id}`;
                            }
                            break;
                            
                        case 'document':
                            try {
                                const doc = await documentsService.getDocument(id, activeTenancy?.id || null);
                                if (doc) displayName = doc.name || doc.title || id;
                                else displayName = `Document ${id}`;
                            } catch (err) {
                                console.warn(`Error fetching document ${id}:`, err);
                                displayName = displayName || `Document ${id}`;
                            }
                            break;
                            
                        default:
                            displayName = displayName || `${component.domain}: ${id}`;
                    }
                } catch (error) {
                    console.error(`Error fetching ${component.domain} ${id}:`, error);
                    displayName = displayName || `${component.domain} ${id}`;
                }
            }
            
            return {
                ...component,
                displayName: displayName || component.title || id || 'Unknown',
                isLoading: false
            };
        });
        
        try {
            const resolved = await Promise.all(enhancedPromises);
            setEnhancedComponents(resolved);
        } catch (error) {
            console.error('Error resolving component details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Group components by domain - use enhancedComponents when available
    const componentsToGroup = enhancedComponents.length > 0 ? enhancedComponents : components;
    const groupedComponents = componentsToGroup?.reduce((acc, component) => {
        const domain = component.domain || 'Unknown';
        if (!acc[domain]) acc[domain] = [];
        acc[domain].push(component);
        return acc;
    }, {} as Record<string, EnhancedComponent[]>) || {};

    // Define color scheme for different domains
    const getDomainColor = (domain: string): string => {
        const colors: Record<string, string> = {
            'Asset': 'bg-blue-100 text-blue-800 hover:bg-blue-200',
            'Assurance': 'bg-amber-100 text-amber-800 hover:bg-amber-200',
            'Policy': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
            'Risk': 'bg-red-100 text-red-800 hover:bg-red-200',
            'Control': 'bg-green-100 text-green-800 hover:bg-green-200',
        };
        return colors[domain] || 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    };

    // Include relatedAssuranceTasks as another domain group if they exist
    const hasAssuranceTasks = Array.isArray(relatedAssuranceTasks) && relatedAssuranceTasks.length > 0;
    
    // Create fake component objects for assurance tasks so they can use the same display logic
    if (hasAssuranceTasks) {
        if (!groupedComponents['Assurance']) {
            groupedComponents['Assurance'] = [];
        }
        
        relatedAssuranceTasks.forEach(taskId => {
            groupedComponents['Assurance'].push({
                confidence: 1,
                domain: 'Assurance',
                id: `internal:${taskId}`,
                ref: taskId || '',
                relation: 'related',
                scheme: 'internal',
                title: '',
                displayName: '' // Will be populated when expanded
            });
        });
    }
    
    // Loading indicator when fetching component details
    if (expanded && isLoading) {
        return (
            <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading component details...</span>
            </div>
        );
    }
    
    // If no components and no assurance tasks, show N/A
    if ((!components || components.length === 0) && !hasAssuranceTasks) {
        return <span className="text-gray-500 italic">N/A</span>;
    }

    // Count of components by domain for compact view
    const domainCounts = Object.entries(groupedComponents).map(([domain, comps]) => (
        <Badge key={domain} className={`${getDomainColor(domain)} mr-1 mb-1`}>
            {domain}: {comps.length}
        </Badge>
    ));

    return (
        <div className="relative">
            <div className="flex items-center">
                <div className="flex flex-wrap items-center gap-1">
                    {domainCounts}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 h-6 w-6 mr-1"
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
            </div>

            {expanded && (
                <div className="mt-2 space-y-2 pl-6 border-l-2 border-gray-200">
                    {Object.entries(groupedComponents).map(([domain, domainComponents]) => (
                        <div key={domain} className="space-y-1">
                            <div className="font-medium text-sm">{domain}</div>
                            <div className="flex flex-wrap gap-1">
                                {domainComponents.map((component) => (
                                    <TooltipProvider key={component.id}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Badge
                                                    className={`${getDomainColor(domain)} cursor-pointer`}
                                                    variant="outline"
                                                >
                                                    {component.displayName ? 
                                                        component.displayName.substring(0, 30) + (component.displayName.length > 30 ? '...' : '') : 
                                                        component.ref + (component.title ? `: ${component.title.substring(0, 15)}${component.title.length > 15 ? '...' : ''}` : '')}
                                                </Badge>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-[300px]">
                                                <div className="space-y-1 text-xs">
                                                    <div><span className="font-medium">ID:</span> {component.id}</div>
                                                    <div><span className="font-medium">Ref:</span> {component.ref}</div>
                                                    <div><span className="font-medium">Relation:</span> {component.relation}</div>
                                                    {component.title && <div><span className="font-medium">Title:</span> {component.title}</div>}
                                                    {/* <div><span className="font-medium">Confidence:</span> {(component.confidence * 100).toFixed(0)}%</div> */}
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ControlListProps {
    controls: Control[];
    selectedControls: string[];
    onSelectControl: (id: string, checked: boolean) => void;
    onSelectAll: (checked: boolean) => void;
    someSelected: boolean;
    allSelected: boolean;
    isLoading: boolean;
    error: string | null;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    currentPage?: number;
    pageSize?: number;
}

const ControlList: React.FC<ControlListProps> = ({
    controls,
    selectedControls,
    onSelectControl,
    onSelectAll,
    someSelected,
    allSelected,
    isLoading,
    error,
    totalCount = 0,
    onPageChange,
    onPageSizeChange,
    currentPage = 0,
    pageSize = 10
}) => {
    if (isLoading) {
        return (
            <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-destructive/10 p-4 rounded-md text-destructive">
                <p>{error}</p>
            </div>
        );
    }

    if (controls.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                <p>No controls found. Create your first control to get started.</p>
            </div>
        );
    }

    const columns: ColumnDef<Control>[] = [
        {
            accessorKey: 'controlID',
            header: 'ID',
            cell: ({ row }) => {
                const control = row.original;
                return <span className="font-medium">{control.controlID}</span>
            }
        },
        {
            accessorKey: 'controlDescription',
            header: 'Description',
            cell: ({ row }) => {
                const control = row.original;
                return <span className="font-medium">{control.controlDescription || control?.controlDesc || control.controlName || 'N/A'}</span>
            }
        },
        {
            accessorKey: 'standard',
            header: 'Standard',
            cell: ({ row }) => {
                const standard = row.original.standard || '';
                const variant = {
                    active: 'bg-green-100 text-green-800',
                    inactive: 'bg-red-100 text-red-800',
                    draft: 'bg-yellow-100 text-yellow-800'
                }[standard] || 'bg-gray-100 text-gray-800';

                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variant}`}>
                        {standard ? (standard.charAt(0).toUpperCase() + standard.slice(1)) : 'N/A'}
                    </span>
                );
            }
        },
        {
            accessorKey: 'linkedComponents',
            header: 'Linked Components',
            cell: ({ row }) => {
                const control = row.original;
                return <LinkedComponentsCell 
                    components={control.linkedComponents || []} 
                    relatedAssuranceTasks={control.relatedAssuranceTasks || []}
                />;
            }
        },
        {
            accessorKey: 'updatedAt',
            header: 'Last Updated',
            cell: ({ row }) => (
                <div className="text-right">
                    {row.original.updatedAt
                        ? cleanDateAndTimeObject(row.original.updatedAt) instanceof Date ? toLocalDateString(cleanDateAndTimeObject(row.original.updatedAt)) : 'Not yet updated'
                        : 'Not yet updated'}
                </div>
            )
        }
    ];

    // Simple debug logging for selection changes
    useEffect(() => {
        console.log(`Selection state changed: ${selectedControls.length} items selected`);
    }, [selectedControls]);

    return (
        <div className="rounded-md">
            <DataTable
                columns={columns}
                data={controls}
                pageSize={pageSize}
                selectable
                onRowSelectionChange={(selectedRows) => {
                    // Get control IDs from selected rows with proper null checks
                    const selectedIds = selectedRows.map(row => {
                        // First check if row and row.original exist
                        // if (!row || !row.original) {
                        //     console.warn('Row or row.original is undefined', row);
                        //     return null;
                        // }
                        
                        const control = row.original || row;
                        console.log('Selected row:', row);
                        
                        // Use controlID if it exists, otherwise fallback to id
                        // This helps handle different control object structures
                        return control.controlID || control.id || null;
                    }).filter(Boolean); // Filter out any undefined/null values
                    
                    console.log('Selected row IDs:', selectedIds);

                    // Reset selection state and set the new selection
                    const currentSelection = new Set(selectedControls);
                    const newSelection = new Set(selectedIds);

                    // Handle removals - IDs in current but not in new
                    selectedControls.forEach(id => {
                        if (!newSelection.has(id)) {
                            onSelectControl(id, false);
                        }
                    });

                    // Handle additions - IDs in new but not in current
                    selectedIds.forEach(id => {
                        if (!currentSelection.has(id)) {
                            onSelectControl(id, true);
                        }
                    });
                }}
            />
        </div>
    );
};

export default ControlList;
